import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  Button,
  Typography,
  Input,
  Space,
  Tag,
  Modal,
  notification,
  Segmented,
  Dropdown,
} from "antd";
import {
  PlusOutlined,
  StarFilled,
  StarOutlined,
  ExclamationCircleOutlined,
  MoreOutlined,
  BookOutlined,
  EditOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getCourseProfessors,
  isCourseOwner,
  isCourseEditor,
  isCourseMember,
} from "../data";
import { useAppContext } from "../context/AppContext";
import * as coursesApi from "../api/courses";
import * as favoritesApi from "../api/favorites";
import CourseEditDrawer from "./CourseEditDrawer";
import PersonChip from "./PersonChip";

const { Title, Text } = Typography;
const { Search } = Input;
const { confirm } = Modal;

const FILTER_ALL = "tous";
const FILTER_FAV = "favoris";
const FILTER_MINE = "mine";
const FILTER_DRAFTS = "drafts";

const CoursesPage = ({
  professorName,
  courseList,
  setCourseList,
  favoriteCourseIds,
  setFavoriteCourseIds,
}) => {
  const navigate = useNavigate();
  const { userRole, user } = useAppContext();
  const isProfessor = userRole === "Professeur";

  const [searchParams, setSearchParams] = useSearchParams();
  const defaultFilter = isProfessor ? FILTER_MINE : FILTER_ALL;
  const filter = searchParams.get("filter") || defaultFilter;

  const [searchText, setSearchText] = useState("");
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);

  // If a non-prof somehow lands on a prof-only filter, snap to default
  useEffect(() => {
    if (!isProfessor && (filter === FILTER_MINE || filter === FILTER_DRAFTS)) {
      setSearchParams({ filter: FILTER_ALL }, { replace: true });
    }
  }, [filter, isProfessor, setSearchParams]);

  const setFilter = (value) => {
    setSearchParams(value === defaultFilter ? {} : { filter: value });
  };

  const toggleFavorite = (courseId) => {
    const isFav = favoriteCourseIds.includes(courseId);
    // Optimistic update; revert on API failure.
    setFavoriteCourseIds((prev) =>
      isFav ? prev.filter((id) => id !== courseId) : [...prev, courseId]
    );
    const call = isFav
      ? favoritesApi.removeCourseFavorite(courseId)
      : favoritesApi.addCourseFavorite(courseId);
    call.catch(() => {
      setFavoriteCourseIds((prev) =>
        isFav ? [...prev, courseId] : prev.filter((id) => id !== courseId)
      );
      notification.error({ message: "Impossible de mettre à jour les favoris" });
    });
  };

  const getCaseCount = (course) => course.caseCount ?? 0;

  const visibleCourses = useMemo(() => {
    // Visibility rules:
    // - Étudiant : seulement les cours publiés
    // - Prof : publiés + ses propres brouillons (et brouillons où il est éditeur)
    return courseList.filter((c) => {
      if (c.status === "published") return true;
      if (!isProfessor) return false;
      return isCourseMember(c, user);
    });
  }, [courseList, isProfessor, professorName]);

  const filteredCourses = useMemo(() => {
    let list = visibleCourses;

    if (filter === FILTER_FAV) {
      list = list.filter((c) => favoriteCourseIds.includes(c.id));
    } else if (filter === FILTER_MINE && isProfessor) {
      list = list.filter((c) => isCourseMember(c, user));
    } else if (filter === FILTER_DRAFTS && isProfessor) {
      list = list.filter(
        (c) => c.status === "draft" && isCourseMember(c, user)
      );
    }

    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          (c.description || "").toLowerCase().includes(q) ||
          getCourseProfessors(c).some((p) => p.toLowerCase().includes(q))
      );
    }

    return list;
  }, [visibleCourses, filter, isProfessor, professorName, searchText, favoriteCourseIds]);

  const handleCreateCourse = () => {
    setEditingCourse(null);
    setEditDrawerOpen(true);
  };

  const handleEditCourse = (course) => {
    setEditingCourse(course);
    setEditDrawerOpen(true);
  };

  const handleSaveCourse = async (values) => {
    try {
      if (editingCourse) {
        const updated = await coursesApi.updateCourse(editingCourse.id, {
          name: values.name,
          description: values.description,
          status: values.status,
          editorIds: values.editorIds,
        });
        setCourseList((prev) =>
          prev.map((c) => (c.id === updated.id ? updated : c))
        );
        notification.success({ message: "Cours mis à jour" });
      } else {
        const created = await coursesApi.createCourse({
          name: values.name,
          description: values.description,
          status: values.status || "draft",
          editorIds: values.editorIds || [],
        });
        setCourseList((prev) => [...prev, created]);
        notification.success({
          message: "Cours créé",
          description: `"${created.name}" — ${created.status === "draft" ? "en brouillon" : "publié"}`,
        });
      }
      setEditDrawerOpen(false);
      setEditingCourse(null);
    } catch (e) {
      notification.error({ message: "Échec de l'enregistrement", description: e.message });
    }
  };

  const handlePublishToggle = async (course) => {
    const next = course.status === "draft" ? "published" : "draft";
    try {
      const updated = await coursesApi.setCourseStatus(course.id, next);
      setCourseList((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      notification.success({
        message: next === "published" ? "Cours publié" : "Cours repassé en brouillon",
      });
    } catch (e) {
      notification.error({ message: "Échec", description: e.message });
    }
  };

  const handleLeaveCourse = (course) => {
    confirm({
      title: `Quitter le cours "${course.name}" ?`,
      icon: <ExclamationCircleOutlined />,
      content: "Vous ne pourrez plus l'éditer. Le propriétaire reste responsable.",
      okText: "Quitter",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          await coursesApi.removeEditor(course.id, user.id);
          setCourseList((prev) =>
            prev.map((c) =>
              c.id === course.id
                ? { ...c, editors: c.editors.filter((e) => e !== professorName) }
                : c
            )
          );
          notification.success({ message: "Vous avez quitté le cours" });
        } catch (e) {
          notification.error({ message: "Échec", description: e.message });
        }
      },
    });
  };

  const handleDeleteCourse = (course) => {
    confirm({
      title: `Supprimer définitivement "${course.name}" ?`,
      icon: <ExclamationCircleOutlined />,
      content: "Cette action est irréversible. Les cas associés ne seront pas supprimés mais seront détachés du cours.",
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          await coursesApi.deleteCourse(course.id);
          setCourseList((prev) => prev.filter((c) => c.id !== course.id));
          notification.success({ message: "Cours supprimé" });
        } catch (e) {
          notification.error({ message: "Échec", description: e.message });
        }
      },
    });
  };

  const buildRowMenu = (course) => {
    const items = [
      { key: "view", label: "Voir le cours" },
      { key: "edit", label: "Modifier" },
    ];
    if (isCourseOwner(course, user)) {
      items.push({
        key: "publish",
        label: course.status === "draft" ? "Publier" : "Repasser en brouillon",
      });
      items.push({ type: "divider" });
      items.push({ key: "delete", label: "Supprimer le cours", danger: true });
    } else if (isCourseEditor(course, user)) {
      items.push({ type: "divider" });
      items.push({ key: "leave", label: "Quitter le cours", danger: true });
    }
    return {
      items,
      onClick: ({ key }) => {
        if (key === "view") navigate(`/cours/${course.id}`);
        else if (key === "edit") handleEditCourse(course);
        else if (key === "publish") handlePublishToggle(course);
        else if (key === "leave") handleLeaveCourse(course);
        else if (key === "delete") handleDeleteCourse(course);
      },
    };
  };

  const segmentedOptions = [
    { label: "Tous", value: FILTER_ALL },
    {
      label: (
        <span>
          <StarFilled style={{ marginRight: 6 }} />
          Favoris
        </span>
      ),
      value: FILTER_FAV,
    },
    ...(isProfessor
      ? [
          {
            label: (
              <span>
                <EditOutlined style={{ marginRight: 6 }} />
                Administrés
              </span>
            ),
            value: FILTER_MINE,
          },
          {
            label: (
              <span>
                <FileTextOutlined style={{ marginRight: 6 }} />
                Brouillons
              </span>
            ),
            value: FILTER_DRAFTS,
          },
        ]
      : []),
  ];

  const columns = [
    {
      title: "",
      key: "favorite",
      width: 48,
      render: (_, course) => {
        const isFav = favoriteCourseIds.includes(course.id);
        return (
          <Button
            type="text"
            icon={
              isFav ? (
                <StarFilled style={{ color: "#fadb14" }} />
              ) : (
                <StarOutlined style={{ color: "#d9d9d9" }} />
              )
            }
            onClick={(e) => {
              e.stopPropagation();
              toggleFavorite(course.id);
            }}
          />
        );
      },
      sorter: (a, b) => {
        const aFav = favoriteCourseIds.includes(a.id) ? 1 : 0;
        const bFav = favoriteCourseIds.includes(b.id) ? 1 : 0;
        return bFav - aFav;
      },
    },
    {
      title: "Cours",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name, course) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          {course.description ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {course.description}
            </Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Professeurs",
      key: "professors",
      render: (_, course) => (
        <Space wrap size={[8, 8]}>
          {getCourseProfessors(course).map((p) => (
            <PersonChip key={p} name={p} />
          ))}
        </Space>
      ),
    },
    {
      title: "Cas",
      key: "cases",
      align: "center",
      width: 80,
      render: (_, course) => getCaseCount(course),
      sorter: (a, b) => getCaseCount(a) - getCaseCount(b),
    },
    {
      title: "Mis à jour",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 130,
      sorter: (a, b) => (a.updatedAt || "").localeCompare(b.updatedAt || ""),
      render: (d) => d || "—",
    },
  ];

  if (isProfessor) {
    columns.push({
      title: "Statut",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status) =>
        status === "draft" ? (
          <Tag color="orange" icon={<FileTextOutlined />}>
            Brouillon
          </Tag>
        ) : (
          <Tag color="green">Publié</Tag>
        ),
      filters: [
        { text: "Brouillon", value: "draft" },
        { text: "Publié", value: "published" },
      ],
      onFilter: (value, record) => record.status === value,
    });

    columns.push({
      title: "",
      key: "actions",
      width: 56,
      align: "center",
      render: (_, course) => {
        if (!isCourseMember(course, user)) return null;
        return (
          <Dropdown menu={buildRowMenu(course)} trigger={["click"]}>
            <Button
              type="text"
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        );
      },
    });
  }

  const buildEmptyState = () => {
    if (filteredCourses.length > 0) return null;
    if (filter === FILTER_FAV) {
      return {
        title: "Aucun cours favori",
        description:
          'Ajoutez un cours à vos favoris en cliquant sur l\'étoile dans la liste "Tous".',
        cta: { label: "Voir tous les cours", action: () => setFilter(FILTER_ALL) },
      };
    }
    if (filter === FILTER_MINE) {
      return {
        title: "Vous ne gérez aucun cours",
        description:
          'Créez votre premier cours, ou demandez à être ajouté comme co-éditeur.',
        cta: { label: "+ Créer un cours", action: handleCreateCourse },
      };
    }
    if (filter === FILTER_DRAFTS) {
      return {
        title: "Aucun brouillon",
        description: "Vos cours en cours de rédaction apparaîtront ici.",
      };
    }
    return {
      title: "Aucun cours",
      description: "Aucun cours ne correspond à votre recherche.",
    };
  };
  const emptyState = buildEmptyState();

  return (
    <div
      style={{
        padding: 20,
        background: "var(--color-bg)",
        color: "var(--color-text)",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <Title level={2} style={{ margin: 0, color: "var(--color-text)" }}>
          <BookOutlined style={{ marginRight: 8 }} />
          Cours
        </Title>
        {isProfessor && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateCourse}>
            Créer un cours
          </Button>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <Segmented
          options={segmentedOptions}
          value={filter}
          onChange={setFilter}
        />
      </div>

      <Search
        placeholder="Rechercher un cours, un professeur, une description..."
        onChange={(e) => setSearchText(e.target.value)}
        size="large"
        style={{ marginBottom: 16, width: "100%" }}
        allowClear
      />

      <Table
        columns={columns}
        dataSource={filteredCourses}
        rowKey="id"
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        onRow={(course) => ({
          onClick: () => navigate(`/cours/${course.id}`),
          style: { cursor: "pointer" },
        })}
        locale={
          emptyState
            ? {
                emptyText: (
                  <div style={{ padding: "32px 16px" }}>
                    <Title level={5} style={{ color: "var(--color-text)" }}>
                      {emptyState.title}
                    </Title>
                    <Text type="secondary">{emptyState.description}</Text>
                    {emptyState.cta && (
                      <div style={{ marginTop: 16 }}>
                        <Button type="primary" onClick={emptyState.cta.action}>
                          {emptyState.cta.label}
                        </Button>
                      </div>
                    )}
                  </div>
                ),
              }
            : undefined
        }
      />

      <CourseEditDrawer
        open={editDrawerOpen}
        course={editingCourse}
        professorName={professorName}
        courseList={courseList}
        onClose={() => {
          setEditDrawerOpen(false);
          setEditingCourse(null);
        }}
        onSubmit={handleSaveCourse}
        onDelete={handleDeleteCourse}
      />
    </div>
  );
};

export default CoursesPage;
